import React, { useEffect, useMemo, useRef, useState } from 'react';
import tenantConstants from '@constants';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { formatNumberString } from '../../../../utility/utility';
import { adLicenseClickEvent } from '../../../../services/analyticsService';
import { PAGE_SECTIONS, INTERACTED_FROM } from '../../../../services/analyticsService/constants';
import { useGetRegaAdLicensesSummaryQuery, useLazyGetRegaAdLicensesQuery } from '../../apis/regaAdLicenses';
import { getCardState, getLicenseTitle, getLicenseLocation, formatIssuedAgo } from './utils';
import { SearchIcon, ChevronRightIcon } from './icons';
import AdLicensesListModal from './ad-licenses-list-modal';
import {
  Root,
  Field,
  IconSlot,
  InputWrap,
  PlaceholderLayer,
  PlaceholderText,
  PlaceholderAlt,
  Input,
  Dropdown,
  DropdownHeader,
  RecentLabel,
  BrowseHint,
  Row,
  RowMain,
  RowTopLine,
  RowNumber,
  IssuedText,
  ChipsLine,
  Chip,
  Price,
  ChevronSlot,
  Footer,
  FooterCount,
  ViewAllButton,
  NumberText,
  NumberDim,
  NumberMark,
} from './ad-license-search-input.styled';

const PLACEHOLDERS = ['Click to see recent active ad licenses against your FAL', 'Or enter your ad license to continue'];
// Static hint shown (no rotation) in the unverified / Verify-FAL state, per design.
const STATIC_PLACEHOLDER = 'Enter your ad license to continue';
const ROTATE_MS = 3200;

const HighlightedNumber = ({ number, query, highlight }) => {
  const value = String(number || '');
  const idx = query ? value.indexOf(query) : -1;
  if (idx < 0) {
    return <NumberText $highlight={highlight}>{value}</NumberText>;
  }
  return (
    <NumberText $highlight={highlight}>
      <NumberDim>{value.slice(0, idx)}</NumberDim>
      <NumberMark>{value.slice(idx, idx + query.length)}</NumberMark>
      {value.slice(idx + query.length)}
    </NumberText>
  );
};

const AdLicenseSearchInput = ({ formik, onPostLicense, onContinueStateChange = () => {}, loading: submitting = false }) => {
  const { t, i18n } = useTranslation();
  const { user: loginUser } = useSelector((state) => state.app.loginUser);

  const { data: summary } = useGetRegaAdLicensesSummaryQuery({});
  const [triggerGetList] = useLazyGetRegaAdLicensesQuery();

  const state = summary ? getCardState(summary) : 'unverified';
  // The recent-licenses dropdown only has data in the pending state.
  const dropdownEnabled = state === 'pending';
  // Rotating placeholder animates where the field is the primary entry point: pending (has licenses)
  // and clear (FAL verified, 0 active). For unverified (Verify FAL) show a static hint instead.
  const rotating = state === 'pending' || state === 'clear';

  const [value, setValue] = useState(formik?.values?.adLicense || '');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [phIndex, setPhIndex] = useState(0);
  const [listOpen, setListOpen] = useState(false);
  const blurTimer = useRef(null);

  // Continue starts disabled while the field is empty (matches the HTML's disabled state).
  useEffect(() => {
    onContinueStateChange(!value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotating placeholder (pending + clear states, empty only). Pause when the field has text.
  useEffect(() => {
    if (!rotating || value) {
      setPhIndex(0);
      return undefined;
    }
    const id = setInterval(() => setPhIndex((prev) => (prev === 0 ? 1 : 0)), ROTATE_MS);
    return () => clearInterval(id);
  }, [rotating, value]);

  const updateContinueState = (val, matchCount, inFlight) => {
    if (!val) return onContinueStateChange(true);
    if (!dropdownEnabled) return onContinueStateChange(false);
    if (inFlight) return onContinueStateChange(true);
    return onContinueStateChange(matchCount >= 1);
  };

  // shouldOpen=true means the call came from a user interaction (focus/type): open the dropdown
  // only if the API actually returned rows, and close it when it returned none. The prime-on-mount
  // call passes shouldOpen=false so it never opens the dropdown on its own.
  const fetchList = async (query, shouldOpen = false) => {
    setLoading(true);
    const params = { per_page: 5, 'q[s]': 'rega_ad_license_issue_date desc' };
    if (query) params['q[rega_ad_license_ad_license_number_text_cont]'] = query;
    const response = await triggerGetList(params);
    setLoading(false);
    const list = response?.data?.list || [];
    const totalCount = response?.data?.pagination?.total_count ?? list.length;
    setResults(list);
    setTotal(totalCount);
    setActiveIndex(0);
    if (shouldOpen) setOpen(list.length > 0);
    updateContinueState(query, list.length, false);
  };

  // Keep a stable debounced function that always calls the latest fetchList (avoids a stale
  // closure capturing the first render's summary/state).
  const fetchListRef = useRef(fetchList);
  fetchListRef.current = fetchList;
  const debouncedFetch = useMemo(() => debounce((query) => fetchListRef.current(query, true), 300), []);
  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // Prime the recent list once, as soon as the dropdown becomes available (summary resolves to
  // the "pending" state), so the data is already loaded before the user focuses the field — this
  // removes the layout "jerk" of the dropdown opening empty and then filling in. The dropdown is
  // still closed at this point, so nothing is shown until focus. Only runs when the dropdown is
  // actually used (pending state), so no extra call is made for other states/flows.
  const primedRef = useRef(false);
  useEffect(() => {
    if (dropdownEnabled && !primedRef.current) {
      primedRef.current = true;
      fetchListRef.current('');
    }
  }, [dropdownEnabled]);

  const handleType = (event) => {
    const next = event.target.value.replace(/\D/g, '').slice(0, 10);
    setValue(next);
    formik.setFieldValue('adLicense', next);
    if (dropdownEnabled) {
      // Don't open eagerly — the debounced fetch opens the dropdown only if it finds data.
      updateContinueState(next, 1, true); // keep disabled while deciding / in flight
      debouncedFetch(next);
    } else {
      updateContinueState(next, 0, false);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    if (dropdownEnabled) {
      adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.RECENT_DROPDOWN });
      // Open only when there is data: if the list was primed on mount, open now; otherwise fetch
      // and let it open only if the API returns rows. Never show an empty dropdown.
      if (results.length) setOpen(true);
      else fetchList(value || '', true);
    }
  };

  const handleBlur = () => {
    setFocused(false);
    blurTimer.current = setTimeout(() => setOpen(false), 140);
  };

  const pickRow = (row, interactedFrom) => {
    if (!row) return;
    setOpen(false);
    // Show the picked licence number in the field right away; the input then greys out + disables
    // via the `submitting` (validation) loading state below while onPostLicense validates it.
    const number = String(row.ad_license_number ?? '');
    setValue(number);
    formik.setFieldValue('adLicense', number);
    adLicenseClickEvent(loginUser, {
      pageSection: PAGE_SECTIONS.RECENT_DROPDOWN,
      interactedFrom,
      value: row.ad_license_number,
    });
    onPostLicense(row.ad_license_number, PAGE_SECTIONS.RECENT_DROPDOWN);
  };

  const handleKeyDown = (event) => {
    if (!open || !results.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => (prev - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pickRow(results[activeIndex], INTERACTED_FROM.KEYBOARD);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const openList = () => {
    setOpen(false);
    adLicenseClickEvent(loginUser, { pageSection: PAGE_SECTIONS.VIEW_ALL });
    setListOpen(true);
  };

  const showPlaceholder = !value;

  return (
    <Root>
      <Field $submitting={submitting} $focused={focused}>
        <IconSlot $focused={focused}>
          <SearchIcon size={17} />
        </IconSlot>
        <InputWrap>
          {showPlaceholder && (
            <PlaceholderLayer>
              <PlaceholderText $rotating={rotating} $phIndex={phIndex}>
                {t(rotating ? PLACEHOLDERS[0] : STATIC_PLACEHOLDER)}
              </PlaceholderText>
              {rotating && (
                <PlaceholderAlt $phIndex={phIndex}>
                  {t(PLACEHOLDERS[1])}
                </PlaceholderAlt>
              )}
            </PlaceholderLayer>
          )}
          <Input
            type="text"
            inputMode="numeric"
            value={value}
            onChange={handleType}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            maxLength={10}
            disabled={submitting}
            $submitting={submitting}
          />
        </InputWrap>
      </Field>

      {open && dropdownEnabled && !submitting && results.length > 0 && (
        <Dropdown
          onMouseDown={(e) => {
            // keep the input focused so a row click registers before blur-close
            e.preventDefault();
            if (blurTimer.current) clearTimeout(blurTimer.current);
          }}
        >
          <DropdownHeader>
            <RecentLabel>{t('RECENT')}</RecentLabel>
            <BrowseHint>{t('↑↓ to browse · Enter selects the top one')}</BrowseHint>
          </DropdownHeader>

          {results.map((row, index) => {
              const isActive = index === activeIndex;
              const typeTitle = getLicenseTitle(row?.property_type, row?.purpose, t);
              const location = getLicenseLocation(row?.location);
              const area = row?.area_unit_value != null ? `${row.area_unit_value} ${t('Sq. M.')}` : '';
              return (
                <Row
                  key={row.id}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => pickRow(row, INTERACTED_FROM.MOUSE)}
                  $active={isActive}
                >
                  <RowMain>
                    <RowTopLine>
                      <RowNumber>
                        <HighlightedNumber number={row.ad_license_number} query={value} highlight={isActive} />
                      </RowNumber>
                      <IssuedText>
                        {t('Issued {{ago}}', { ago: formatIssuedAgo(row.issue_date, i18n.language) })}
                      </IssuedText>
                    </RowTopLine>
                    <ChipsLine>
                      {typeTitle ? <Chip>{typeTitle}</Chip> : null}
                      {location ? <Chip>{location}</Chip> : null}
                      {area ? <Chip>{area}</Chip> : null}
                    </ChipsLine>
                  </RowMain>
                  <Price $active={isActive}>
                    {tenantConstants.CURRENCY_SYMBOL()} {formatNumberString(row.price)}
                  </Price>
                  {isActive && (
                    <ChevronSlot>
                      <ChevronRightIcon size={16} />
                    </ChevronSlot>
                  )}
                </Row>
              );
            })}

          <Footer>
            <FooterCount>
              {t('Showing {{n}} of {{total}}', { n: results.length, total: formatNumberString(total) })}
            </FooterCount>
            <ViewAllButton type="button" onClick={openList}>
              {t('View all')}
              <ChevronRightIcon size={14} />
            </ViewAllButton>
          </Footer>
        </Dropdown>
      )}

      <AdLicensesListModal
        visible={listOpen}
        onClose={() => setListOpen(false)}
        onPostLicense={onPostLicense}
        summary={summary}
      />
    </Root>
  );
};

export default AdLicenseSearchInput;
